import { Icon } from '@iconify/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { signIn } from '~features/sign-in/api'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '~in-app/components'
import { auth$, route$ } from '~in-app/legend-state'
import { signInValidation } from '~in-app/validation'

export const Route = createFileRoute('/(public)/_public/sign-in')({
  pendingComponent: () => <div>Loading...</div>,
  component: SignInPage,
  staticData: {
    privileges: [],
  },
})

function SignInPage() {
  const router = useRouter()
  const [isVisible, toggleVisibility] = useState(false)
  const signInMutation = useMutation({
    mutationFn: signIn,
    onSuccess: (data) => {
      if (data) {
        Promise.all([
          auth$.jwt.set(data?.token || null),
          auth$.username.set(data?.username),
          auth$.multiTenancyEnabled.set(data?.multiTenancyEnabled),
          auth$.division.set(data?.division),
          auth$.firstName.set(data?.firstName),
          auth$.operator.set(data?.operator),
          auth$.clientId.set(data?.clientId),
          auth$.privileges.set(
            data?.privileges.reduce((previousValue: any, currentValue: any) => {
              currentValue.privilegeIds.forEach((privilege: any) => {
                previousValue.set(privilege, {
                  divisionIds: [
                    ...(previousValue.get(privilege)?.divisionIds || []),
                    ...currentValue.divisionIds,
                  ],
                  firstParentDivisionIds: [
                    ...(previousValue.get(privilege)?.firstParentDivisionIds || []),
                    ...currentValue.firstParentDivisionIds,
                  ],
                })
              })

              return previousValue
            }, new Map()),
          ),
        ]).then(() => {
          router.navigate({ to: route$.fallbackPath('auth') === '/console/no-permission' ? '/console' : route$.fallbackPath('auth') })
        })
        toast.success('Login successful', { position: 'top-center', hideProgressBar: false, autoClose: 500 })
      }
    },
    onError: (error: Error) => {
      console.error('Login failed:', error)
      toast.error('Invalid username or password', { position: 'top-center', hideProgressBar: false, autoClose: 500 })
    },
  })
  const formOpts = formOptions({ defaultValues: {
    username: '',
    password: '',
  },
  validatorAdapter: zodValidator(),
  onSubmit: async ({ value }) => {
    try {
      signInMutation.mutate(value)
    }
    catch (error) {
      console.error('Login failed:', error)
      toast.error('Invalid username or password', { position: 'top-center', hideProgressBar: false, autoClose: 500 })
    }
  },
  })
  const formControl = useForm(formOpts)

  return (
    <Card className="relative z-10 w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-2xl">Sign in</CardTitle>
        <CardDescription>Enter your email below to login to your account</CardDescription>
      </CardHeader>

      <form
        noValidate
        onSubmit={
          (e) => {
            e.preventDefault()
            formControl.handleSubmit()
          }
        }
      >
        <CardContent className="grid gap-4">
          <div className="grid gap-2 ">
            <Label
              required
              htmlFor="username"
            >
              Username
            </Label>

            <formControl.Field
              name="username"
              children={
                (field) => {
                  return (
                    <>
                      <Input
                        id="email"
                        type="string"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={e => field.handleChange(e.target.value)}
                      />

                      {
                        field.state.meta.errors
                          ? (
                              <em
                                className="error-message"
                                role="alert"
                              >
                                {field.state.meta.errors.join(', ')}
                              </em>
                            )
                          : null
                      }
                    </>

                  )
                }
              }
              validators={
                {
                  onChange: signInValidation.shape.username,
                }
              }

            />

          </div>

          <div className="grid gap-2">
            <Label
              required
              htmlFor="password"
            >
              Password
            </Label>

            <formControl.Field
              name="password"
              children={
                (field) => {
                  return (
                    <>
                      <div className="relative flex w-full">
                        <Input
                          className="pr-10" // Add padding on the right to avoid text overlap with the icon
                          id="password"
                          type={isVisible ? 'text' : 'password'}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={e => field.handleChange(e.target.value)}
                        />

                        {/* Eye toggle button inside the input */}
                        <button
                          className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center text-sm"
                          type="button"
                          onClick={() => toggleVisibility(!isVisible)}
                        >
                          <Icon
                            height={24}
                            icon={isVisible ? 'mdi:eye' : 'mdi:eye-off'}
                            width={24}
                          />
                        </button>
                      </div>

                      {
                        field.state.meta.errors && (
                          <em
                            className="error-message"
                            role="alert"
                          >
                            {field.state.meta.errors.join(', ')}
                          </em>
                        )
                      }
                    </>
                  )
                }
              }
              validators={
                {
                  onChange: signInValidation.shape.password,
                }
              }
            />

          </div>

          <span className="flex justify-end">
            <Link
              className="text-right text-sm underline"
              to="/forgot-password"
            >
              Forgot password?
            </Link>
          </span>
        </CardContent>

        <CardFooter>
          <formControl.Subscribe
            selector={state => [state.canSubmit, state.isSubmitting]}
            children={
              ([canSubmit, isSubmitting]) => {
                return (
                  <Button
                    fullWidth
                    disabled={!canSubmit}
                  >
                    {isSubmitting ? '...' : 'Sign In'}
                  </Button>
                )
              }
            }
          />

        </CardFooter>
      </form>
    </Card>
  )
}
